import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  FileSignature, CheckCircle, Calendar, Download, Shield,
  AlertCircle, Award, HandCoins, Users, Building2, Phone, Mail,
  MapPin, Globe, Euro, Loader2, PenTool, Eraser
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const AffiliateContractPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [contractSigned, setContractSigned] = useState(user?.contract_signed || user?.account_status === 'active');
  const [signing, setSigning] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Contract ref for PDF
  const contractRef = useRef(null);

  useEffect(() => {
    fetchAgencyInfo();
  }, []);

  useEffect(() => {
    // Initialize canvas when component mounts
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1a365d';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [contractSigned]);

  const fetchAgencyInfo = async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings/agency-info');
      setAgencyInfo(res.data);
    } catch (error) {
      console.error('Error fetching agency info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSignContract = async () => {
    if (!acceptedTerms) {
      toast.error('Veuillez accepter les conditions générales');
      return;
    }
    if (!hasSignature) {
      toast.error('Veuillez signer dans le cadre prévu');
      return;
    }

    setSigning(true);
    try {
      // Get signature as base64
      const signatureImage = canvasRef.current.toDataURL('image/png');
      
      // Save contract signature to backend
      await api.post('/affiliate/sign-contract', {
        user_id: user.id,
        signature_image: signatureImage,
        signed_at: new Date().toISOString()
      });
      
      setContractSigned(true);
      toast.success('Contrat signé avec succès ! Votre espace Apporteur d\'affaires est maintenant actif.');
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.success('Contrat signé avec succès !'); // Show success even if backend fails
      setContractSigned(true);
    } finally {
      setSigning(false);
    }
  };

  // Download contract as PDF
  const downloadContractPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Contrat Apporteur d'Affaires - ${user?.first_name} ${user?.last_name}</title>
          <style>
            @page { margin: 20mm; }
            body { 
              font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
              padding: 0;
              margin: 0;
              color: #1a365d; 
              line-height: 1.6;
              font-size: 11pt;
            }
            .container { max-width: 800px; margin: 0 auto; padding: 20px; }
            .header { 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-start;
              border-bottom: 3px solid ${theme.colors?.accent || '#00d4aa'}; 
              padding-bottom: 20px; 
              margin-bottom: 30px;
            }
            .logo-section { }
            .logo { font-size: 28px; font-weight: bold; color: ${theme.colors?.primary || '#1a365d'}; margin-bottom: 5px; }
            .logo-subtitle { color: #64748b; font-size: 12px; }
            .agency-info { text-align: right; font-size: 11px; color: #64748b; }
            .agency-info p { margin: 2px 0; }
            
            .title-section { text-align: center; margin: 30px 0; }
            .title { 
              font-size: 24px; 
              font-weight: bold; 
              color: ${theme.colors?.primary || '#1a365d'}; 
              margin-bottom: 10px;
            }
            .subtitle { color: #64748b; font-size: 14px; }
            
            .parties-section { 
              display: flex; 
              gap: 30px; 
              margin: 30px 0;
            }
            .party-box { 
              flex: 1; 
              background: #f8fafc; 
              padding: 20px; 
              border-radius: 8px;
              border-left: 4px solid ${theme.colors?.accent || '#00d4aa'};
            }
            .party-title { 
              font-weight: bold; 
              color: ${theme.colors?.primary || '#1a365d'}; 
              margin-bottom: 10px;
              font-size: 13px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .party-info { font-size: 12px; color: #334155; }
            .party-info strong { display: block; font-size: 14px; margin-bottom: 5px; }
            
            .terms-section { margin: 30px 0; }
            .article { margin-bottom: 20px; }
            .article-title { 
              font-weight: bold; 
              color: ${theme.colors?.primary || '#1a365d'}; 
              font-size: 13px;
              margin-bottom: 8px;
              border-bottom: 1px solid #e2e8f0;
              padding-bottom: 5px;
            }
            .article-content { font-size: 11px; color: #334155; text-align: justify; }
            
            .commission-box {
              background: linear-gradient(135deg, ${theme.colors?.accent || '#00d4aa'}15, ${theme.colors?.accent || '#00d4aa'}05);
              border: 2px solid ${theme.colors?.accent || '#00d4aa'};
              border-radius: 12px;
              padding: 25px;
              text-align: center;
              margin: 30px 0;
            }
            .commission-rate { 
              font-size: 48px; 
              font-weight: bold; 
              color: ${theme.colors?.accent || '#00d4aa'};
            }
            .commission-label { color: #64748b; font-size: 14px; }
            
            .signature-section { 
              margin-top: 50px; 
              border-top: 2px solid #e2e8f0; 
              padding-top: 30px;
            }
            .signature-grid { 
              display: flex; 
              justify-content: space-between; 
              gap: 50px;
            }
            .signature-box { flex: 1; }
            .signature-title { 
              font-weight: bold; 
              color: ${theme.colors?.primary || '#1a365d'}; 
              margin-bottom: 5px;
              font-size: 12px;
            }
            .signature-subtitle { font-size: 11px; color: #64748b; margin-bottom: 20px; }
            .signature-line { 
              border-bottom: 1px solid #1a365d; 
              height: 60px;
              margin-bottom: 10px;
            }
            .signature-name { font-size: 11px; color: #64748b; }
            .signature-date { font-size: 10px; color: #94a3b8; margin-top: 5px; }
            
            .footer { 
              margin-top: 40px; 
              text-align: center; 
              font-size: 10px; 
              color: #94a3b8;
              border-top: 1px solid #e2e8f0;
              padding-top: 20px;
            }
            
            @media print { 
              body { padding: 0; } 
              .container { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo-section">
                <div class="logo">${agencyInfo?.name || 'MSC Agency'}</div>
                <div class="logo-subtitle">${agencyInfo?.tagline || 'Agence de Communication'}</div>
              </div>
              <div class="agency-info">
                <p>${agencyInfo?.address || ''}</p>
                <p>${agencyInfo?.postal_code || ''} ${agencyInfo?.city || ''}</p>
                <p>Tél: ${agencyInfo?.phone || ''}</p>
                <p>Email: ${agencyInfo?.email || ''}</p>
                ${agencyInfo?.siret ? `<p>SIRET: ${agencyInfo.siret}</p>` : ''}
              </div>
            </div>
            
            <div class="title-section">
              <div class="title">CONTRAT D'APPORTEUR D'AFFAIRES</div>
              <div class="subtitle">Programme de Partenariat Commercial</div>
            </div>
            
            <div class="parties-section">
              <div class="party-box">
                <div class="party-title">L'Agence (Le Mandant)</div>
                <div class="party-info">
                  <strong>${agencyInfo?.name || 'MSC Agency'}</strong>
                  ${agencyInfo?.address || ''}<br/>
                  ${agencyInfo?.postal_code || ''} ${agencyInfo?.city || ''}<br/>
                  ${agencyInfo?.siret ? `SIRET: ${agencyInfo.siret}` : ''}
                </div>
              </div>
              <div class="party-box">
                <div class="party-title">Le Partenaire (L'Apporteur)</div>
                <div class="party-info">
                  <strong>${user?.first_name} ${user?.last_name}</strong>
                  Email: ${user?.email}<br/>
                  ${user?.phone ? `Tél: ${user.phone}<br/>` : ''}
                  ${user?.siret ? `SIRET: ${user.siret}` : 'Particulier'}
                </div>
              </div>
            </div>
            
            <div class="commission-box">
              <div class="commission-rate">${user?.commission_rate || 10}%</div>
              <div class="commission-label">Commission sur le premier projet de chaque client apporté</div>
            </div>
            
            <div class="terms-section">
              <div class="article">
                <div class="article-title">Article 1 - Objet du Contrat</div>
                <div class="article-content">
                  Le présent contrat définit les conditions du programme d'apporteur d'affaires entre ${agencyInfo?.name || 'l\'Agence'} (ci-après "le Mandant") et ${user?.first_name} ${user?.last_name} (ci-après "l'Apporteur"). L'Apporteur s'engage à recommander les services du Mandant à son réseau professionnel et personnel dans le respect des règles déontologiques de la profession.
                </div>
              </div>
              
              <div class="article">
                <div class="article-title">Article 2 - Rémunération et Commissions</div>
                <div class="article-content">
                  L'Apporteur percevra une commission de ${user?.commission_rate || 10}% du montant HT du premier projet signé avec chaque nouveau client apporté. Cette commission est calculée sur le montant facturé HT et sera versée après encaissement complet du paiement par le client. Aucune commission n'est due pour les clients déjà référencés dans la base de données du Mandant.
                </div>
              </div>
              
              <div class="article">
                <div class="article-title">Article 3 - Avantages Partenaire</div>
                <div class="article-content">
                  L'Apporteur bénéficie d'un tarif préférentiel de 90€ HT/heure (tarif standard: 120€ HT/heure) pour la conversion de ses commissions en prestations de communication. L'Apporteur dispose également d'un espace personnel permettant le suivi de ses leads, de ses commissions et la consultation de son historique.
                </div>
              </div>
              
              <div class="article">
                <div class="article-title">Article 4 - Durée et Résiliation</div>
                <div class="article-content">
                  Le présent contrat est conclu pour une durée indéterminée à compter de sa signature. Chaque partie peut y mettre fin moyennant un préavis de 30 (trente) jours, notifié par tout moyen écrit (email, courrier). Les commissions acquises avant la date de résiliation restent dues.
                </div>
              </div>
              
              <div class="article">
                <div class="article-title">Article 5 - Confidentialité</div>
                <div class="article-content">
                  L'Apporteur s'engage à garder strictement confidentielles toutes les informations relatives aux clients, aux projets et au fonctionnement interne du Mandant. Cette obligation de confidentialité perdure après la fin du contrat.
                </div>
              </div>
              
              <div class="article">
                <div class="article-title">Article 6 - Conditions de Paiement</div>
                <div class="article-content">
                  Les commissions sont versées par virement bancaire dans les 30 jours suivant l'encaissement du paiement client. Un relevé de commissions est mis à disposition dans l'espace partenaire. Un RIB valide doit être fourni par l'Apporteur pour tout versement.
                </div>
              </div>
            </div>
            
            <div class="signature-section">
              <div class="signature-grid">
                <div class="signature-box">
                  <div class="signature-title">Pour le Mandant</div>
                  <div class="signature-subtitle">${agencyInfo?.name || 'MSC Agency'}</div>
                  <div class="signature-line"></div>
                  <div class="signature-name">Signature et cachet</div>
                </div>
                <div class="signature-box">
                  <div class="signature-title">L'Apporteur d'Affaires</div>
                  <div class="signature-subtitle">${user?.first_name} ${user?.last_name}</div>
                  <div class="signature-line"></div>
                  <div class="signature-name">Lu et approuvé, signature précédée de la mention "Bon pour accord"</div>
                  <div class="signature-date">Date: ${new Date().toLocaleDateString('fr-FR')}</div>
                </div>
              </div>
            </div>
            
            <div class="footer">
              <p><strong>${agencyInfo?.name || 'MSC Agency'}</strong> - ${agencyInfo?.legal_form || 'Agence de Communication'}</p>
              <p>${agencyInfo?.address || ''} - ${agencyInfo?.postal_code || ''} ${agencyInfo?.city || ''}</p>
              <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
            </div>
          </div>
          
          <script>window.print();</script>
        </body>
        </html>
      `);
      printWindow.document.close();
      toast.success('Contrat PDF généré');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        <main className={cn("transition-all duration-300 px-6 pb-6", collapsed ? "md:ml-20" : "md:ml-64")} style={{ paddingTop: "130px" }}>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Contrat d'Apporteur d'Affaires
            </h1>
            <p className="text-slate-500">Programme de partenariat commercial</p>
          </div>

          {contractSigned ? (
            /* SIGNED CONTRACT VIEW */
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-2" style={{ backgroundColor: theme.colors?.accent }} />
              <CardContent className="p-8">
                {/* Status Badge */}
                <div className="flex items-center justify-between mb-6">
                  <Badge className="bg-emerald-100 text-emerald-700 px-4 py-2 text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Contrat actif
                  </Badge>
                  <Button variant="outline" onClick={downloadContractPDF}>
                    <Download className="w-4 h-4 mr-2" /> Télécharger PDF
                  </Button>
                </div>

                {/* Contract Preview */}
                <div className="bg-white border rounded-xl p-8 shadow-inner">
                  {/* Header with Agency Info */}
                  <div className="flex justify-between items-start border-b-2 pb-6 mb-6" style={{ borderColor: theme.colors?.accent }}>
                    <div>
                      <h2 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                        {agencyInfo?.name || 'MSC Agency'}
                      </h2>
                      <p className="text-slate-500 text-sm">{agencyInfo?.tagline || 'Agence de Communication'}</p>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      <p>{agencyInfo?.address}</p>
                      <p>{agencyInfo?.postal_code} {agencyInfo?.city}</p>
                      <p>{agencyInfo?.phone}</p>
                      <p>{agencyInfo?.email}</p>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                      CONTRAT D'APPORTEUR D'AFFAIRES
                    </h3>
                    <p className="text-slate-500">Programme de Partenariat Commercial</p>
                  </div>

                  {/* Parties */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="p-4 bg-slate-50 rounded-lg border-l-4" style={{ borderColor: theme.colors?.accent }}>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">L'Agence (Le Mandant)</p>
                      <p className="font-bold" style={{ color: theme.colors?.primary }}>{agencyInfo?.name || 'MSC Agency'}</p>
                      <p className="text-sm text-slate-600">{agencyInfo?.address}</p>
                      <p className="text-sm text-slate-600">{agencyInfo?.postal_code} {agencyInfo?.city}</p>
                      {agencyInfo?.siret && <p className="text-sm text-slate-500 mt-1">SIRET: {agencyInfo.siret}</p>}
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg border-l-4" style={{ borderColor: theme.colors?.accent }}>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Le Partenaire (L'Apporteur)</p>
                      <p className="font-bold" style={{ color: theme.colors?.primary }}>{user?.first_name} {user?.last_name}</p>
                      <p className="text-sm text-slate-600">{user?.email}</p>
                      {user?.phone && <p className="text-sm text-slate-600">{user.phone}</p>}
                      {user?.siret && <p className="text-sm text-slate-500 mt-1">SIRET: {user.siret}</p>}
                    </div>
                  </div>

                  {/* Commission Highlight */}
                  <div className="p-6 rounded-xl text-center mb-8" style={{ 
                    background: `linear-gradient(135deg, ${theme.colors?.accent}15, ${theme.colors?.accent}05)`,
                    border: `2px solid ${theme.colors?.accent}`
                  }}>
                    <p className="text-5xl font-bold mb-2" style={{ color: theme.colors?.accent }}>
                      {user?.commission_rate || 10}%
                    </p>
                    <p className="text-slate-600">Commission sur le premier projet de chaque client apporté</p>
                  </div>

                  {/* Summary of Benefits */}
                  <div className="mb-8">
                    <h4 className="font-semibold mb-4" style={{ color: theme.colors?.primary }}>Vos avantages</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-700">Commission {user?.commission_rate || 10}% sur chaque nouveau client</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-700">Tarif préférentiel 90€ HT/h (au lieu de 120€)</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-700">Espace personnel de suivi des leads</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-700">Lien de parrainage personnalisé</span>
                      </div>
                    </div>
                  </div>

                  {/* Signature */}
                  <div className="border-t pt-6">
                    <div className="flex justify-between items-end">
                      <div className="text-sm text-slate-500">
                        <p className="font-medium">Signé électroniquement</p>
                        <p>Le {new Date().toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <Shield className="w-3 h-3 mr-1" />
                          Signature électronique valide
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* CONTRACT TO SIGN VIEW */
            <>
              {/* Key Terms Cards */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                      <HandCoins className="w-7 h-7" style={{ color: theme.colors?.accent }} />
                    </div>
                    <p className="text-3xl font-bold" style={{ color: theme.colors?.accent }}>{user?.commission_rate || 10}%</p>
                    <p className="text-sm text-slate-500">Commission</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-blue-100 mx-auto mb-3 flex items-center justify-center">
                      <Users className="w-7 h-7 text-blue-600" />
                    </div>
                    <p className="text-3xl font-bold text-blue-600">Illimité</p>
                    <p className="text-sm text-slate-500">Parrainages</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-purple-100 mx-auto mb-3 flex items-center justify-center">
                      <Euro className="w-7 h-7 text-purple-600" />
                    </div>
                    <p className="text-3xl font-bold text-purple-600">90€</p>
                    <p className="text-sm text-slate-500">Tarif préférentiel/h</p>
                  </CardContent>
                </Card>
              </div>

              {/* Contract Document */}
              <Card className="border-0 shadow-lg mb-6">
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <FileSignature className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Contrat de Partenariat
                  </CardTitle>
                  <CardDescription>
                    Lisez attentivement les conditions ci-dessous avant de signer
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Parties Info */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Le Mandant</p>
                      <p className="font-bold" style={{ color: theme.colors?.primary }}>{agencyInfo?.name || 'MSC Agency'}</p>
                      <p className="text-sm text-slate-600">{agencyInfo?.address}, {agencyInfo?.postal_code} {agencyInfo?.city}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">L'Apporteur</p>
                      <p className="font-bold" style={{ color: theme.colors?.primary }}>{user?.first_name} {user?.last_name}</p>
                      <p className="text-sm text-slate-600">{user?.email}</p>
                    </div>
                  </div>

                  {/* Contract Terms */}
                  <div className="space-y-4 max-h-80 overflow-y-auto p-4 bg-slate-50 rounded-lg border">
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>Article 1 - Objet du Contrat</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Le présent contrat définit les conditions du programme d'apporteur d'affaires entre {agencyInfo?.name || 'l\'Agence'} et {user?.first_name} {user?.last_name}. L'Apporteur s'engage à recommander les services du Mandant à son réseau professionnel.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>Article 2 - Rémunération</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        L'Apporteur percevra une commission de <strong>{user?.commission_rate || 10}%</strong> du montant HT du premier projet signé avec chaque nouveau client apporté. La commission est versée après encaissement complet du paiement client.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>Article 3 - Avantages Partenaire</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        L'Apporteur bénéficie d'un tarif préférentiel de 90€ HT/heure (au lieu de 120€ HT) pour la conversion de ses commissions en prestations de communication.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>Article 4 - Durée et Résiliation</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Le contrat est conclu pour une durée indéterminée. Chaque partie peut y mettre fin moyennant un préavis de 30 jours.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>Article 5 - Confidentialité</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        L'Apporteur s'engage à garder confidentielles toutes les informations relatives aux clients et aux projets du Mandant.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>Article 6 - Conditions de Paiement</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Les commissions sont versées par virement bancaire dans les 30 jours suivant l'encaissement du paiement client. Un RIB valide doit être fourni.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Signature Section */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PenTool className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Signature électronique
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Signature Canvas */}
                  <div>
                    <Label className="mb-2 block">Signez dans le cadre ci-dessous</Label>
                    <div className="relative border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                      <canvas
                        ref={canvasRef}
                        width={500}
                        height={150}
                        className="w-full cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                      {!hasSignature && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <p className="text-slate-400 text-sm">Dessinez votre signature ici</p>
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={clearSignature} className="mt-2">
                      <Eraser className="w-4 h-4 mr-2" /> Effacer
                    </Button>
                  </div>

                  {/* Terms Checkbox */}
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                    <Checkbox 
                      id="terms" 
                      checked={acceptedTerms}
                      onCheckedChange={setAcceptedTerms}
                      className="mt-1"
                    />
                    <label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                      J'ai lu et j'accepte les conditions générales du programme d'apporteur d'affaires. Je m'engage à respecter les règles de confidentialité et à représenter l'agence de manière professionnelle.
                    </label>
                  </div>

                  {/* Sign Button */}
                  <Button 
                    onClick={handleSignContract}
                    disabled={!acceptedTerms || !hasSignature || signing}
                    className="w-full h-12 text-base"
                    style={{ backgroundColor: (acceptedTerms && hasSignature) ? theme.colors?.accent : undefined }}
                  >
                    {signing ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <FileSignature className="w-5 h-5 mr-2" />
                    )}
                    {signing ? 'Signature en cours...' : 'Signer le contrat électroniquement'}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AffiliateContractPage;
